<svelte:options tag={null} />
<script lang="typescript">
  import "@everymatrix/em-card/dist/em-card";
  import "./PlayerConsentLoader.svelte";
  import axios from "axios";
  import type {
    GetUserConsentResponse,
    UserConsent,
		UserConsentForm,
  } from "./PlayerConsentCard.types";
  import { isPermissionPresent } from '@everymatrix/retail-helpers';

  export let src: string = null;
  export let consentsrc: string = null;

  let consents: UserConsent[];
  let consentsForm: UserConsentForm[];
	let edit: boolean = false; 
	let expire: boolean = false;
	let selectedExpire: UserConsentForm;
  let isLoading = false;
  let successMessage: string;
  let errorMessage: string;
  let permissions;
  permissions = {};

  const statusToBoolean = (status: string): boolean => status === "Accepted";


  const getUserConsents = async (consentsrc: string) => {
    try {
      successMessage = null;
      errorMessage = null;
      isLoading = true;
      const headers = await getHeaders();
      const { data, status }: { data: GetUserConsentResponse; status: number } =
        await axios.get(consentsrc, { headers });
      if (status === 200) {
        consents = [...data.items];
        consentsForm = [...data.items.map((consent) => (
          {
            tagCode: consent.tagCode, 
            status: statusToBoolean(consent.status),
            friendlyName: consent.friendlyName
          }
        ))]
      } else {
        throw new Error("Failed to fetch user consents");
      }
    } catch (error) {
      errorMessage = "Failed to fetch user consents";
    } finally {
      isLoading = false;
    }
  };

  const updateUserConsents = async (newConsents: UserConsentForm[]) => {
    try {
			successMessage = null;
			errorMessage = null;
      isLoading = true;
      const headers = await getHeaders();
      const { data, status } = await axios.post(consentsrc, 
        { 
            items: [...newConsents.map(consent => ({tagCode: consent.tagCode, status: consent.status ? 'Accepted': 'Expired'}))]
        }, 
        {headers});
      if(status === 200){
        successMessage = 'User consents updated'
      }else{
        throw new Error("Failed to update user consents");
      }
    } catch (error) {
      errorMessage = "Failed to update user consents";
    }finally{
      isLoading = false;
			edit = false;
    }
  }

	const expireUserConesents = async (tagCode: UserConsent['tagCode']) => {
		try {
			successMessage = null;
			errorMessage = null;
			isLoading = true;
			const headers = await getHeaders();
			const { data, status } = await axios.delete(`${consentsrc}/${tagCode}`, { headers })
			if( status === 200){
				successMessage = 'Consent Expired'
        await getUserConsents(consentsrc);
        selectedExpire = consentsForm.find(consent => consent.status);
			} else {
				throw new Error("Failed to expire consent");
			}
		} catch (error) {
			errorMessage = "Failed to expire consent";
      console.log("error", error);
		} finally {
			isLoading = false;
			expire = false;
		}
	}

	const cancelEdit = () => {
		edit = false;
		consentsForm = [...consents.map((consent) => ({
			tagCode: consent.tagCode, 
      status: statusToBoolean(consent.status),
			friendlyName: consent.friendlyName
		}))];
	}

  const hasAcceptedConsents = () => {
    return consentsForm.map(consent =>  consent.status).includes(true)
  }

  const getHeaders = () => {
    const headerHandler = (e: MessageEvent, resolve: Function, ref: any) => {
      if (e.data?.type === "SetAuthHeaders") {
        // Resolve the promise
        window.removeEventListener("message", ref);
        resolve(e.data.payload);
        // Remove event listner
      }
    };
    // Send the post message to get auth headers
    window.postMessage({ type: "GetAuthHeaders" }, "*");
    return new Promise<HeadersInit>((resolve, reject) => {
      // Create a referance for that function in order to remove the event listener
      let ref = (e) => headerHandler(e, resolve, ref);
      console.log("%c...waiting for auth headers", "color: #135db1");
      // Add event listener to get SetAuthHeaders
      window.addEventListener("message", ref);
    });
  };

  const getPemissions = async () => {
    permissions.canEditConsents = await isPermissionPresent(src, 'upp.dashboard.user-consents', 'WRITE');
  }

  $: src && getPemissions();
	$: consentsrc && getUserConsents(consentsrc);
</script>
<!-- CLOSE -->


<!-- CHECK -->


<em-card>
  <!-- Header -->
  <div slot="header">
    <h3>
      {#if expire}
        Expire user consents
      {:else}
        {#if edit}
          Update user consents
        {:else}
          User consents
        {/if}
      {/if}
    </h3>
  </div>
  <!-- Content -->
  <div 
    slot="content" 
    class="consents"
  >
    {#if isLoading}
      <player-consent-loader />
    {:else}
      {#if consentsForm}
				{#if expire}
          <div class="wrapper">
            <select bind:value={selectedExpire}>
              {#each consentsForm as consent}
                  <option value={consent}>
                    {consent.friendlyName}
                  </option>
              {/each}
            </select>
          </div>
				{:else}
          <ul class="consents__list">
            {#each consentsForm as consent}
              {#if edit}
                <li>
                  <input type="checkbox" bind:checked={consent.status}/>
                  <span>{consent.friendlyName}</span>
                </li>
              {:else if !edit}
                <li class="consents__item">
                  {#if consent.status}
                    <svg class="accepted" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                    </svg>
                  {:else}
                    <svg class="expired" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                    </svg>
                  {/if}
                  <span>{consent.friendlyName}</span>
                </li>
              {/if}
            {/each}
          </ul>
          {#if !hasAcceptedConsents() && !edit}
            <div class="wrapper">
              <span>No accepted consents</span>
            </div>
          {/if}
				{/if}
      {/if}
    {/if}
    {#if successMessage}
			<div class="message__success">
				{successMessage}
			</div>
    {/if}
		{#if errorMessage}
			<div class="message__error">
				{errorMessage}
			</div>
		{/if}
    
    <!-- Actions -->
    {#if permissions.canEditConsents}
      <div class="actions">
        {#if expire}
          <button class="actions__primary" on:click="{()=>{expire = false}}">Cancel</button>
          <button class="actions__primary" on:click="{()=>{expireUserConesents(selectedExpire.tagCode)}}">Expire</button>
        {:else}
          {#if edit}
            <button class="actions__primary" on:click="{()=>{cancelEdit()}}">Cancel</button>
            <button class="actions__primary" on:click="{()=>{updateUserConsents(consentsForm)}}">Save</button>
          {:else}
            <button 
              class:actions__primary={consentsForm?.length > 0 } 
              class:actions__disabled={consentsForm?.length === 0 || !consentsForm } 
              disabled={consentsForm?.length === 0} 
              on:click="{()=>{expire = true}}"
            >
              Expire Consents
            </button>
            <button 
              class:actions__primary={consentsForm?.length > 0 } 
              class:actions__disabled={consentsForm?.length === 0 || !consentsForm} 
              disabled={consentsForm?.length === 0} 
              on:click="{()=>{edit = true}}"
            >
              Update Consents
            </button>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
</em-card>

<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    text-decoration: none;
    outline: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
    color: rgb(80, 98, 133);
  }

  .consents {
    padding-bottom: 15px;

    &__list {

    }

    &__item {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      &:last-child { margin-bottom: 0; }

      .accepted { fill: green; }
      .expired { fill: red; }
      svg { 
        width: 20px; 
        height: 20px; 
        margin-right: 10px;
      }
    }

    label {
      padding: 5px;
      display: flex;
      align-items: center;
      span {
        padding-left: 5px;
        font-size: 16px;
      }
    }
    input {
      height: 16px;
      width: 16px;
      align-self: center;
      &:hover {
        cursor: pointer;
      }
    }
    select {
      border: 1px solid #307fe2;
      border-radius: 5px;
      color: #506285;
      background: 0 0;
      width: 100%;
      padding: 2px;
    }
  }
  .actions {
    margin-top: 20px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: center;
    padding: 5px;

    button {
      width: 100%;
      margin-bottom: 15px;
      min-height: 30px;
      padding: 6px 7px;
      border-radius: 3px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      line-height: 16px;
    }
    
    &__secundary {
      color: #506285;
      border: 1px solid #506285;
      background: #ffffff00;
      &:hover {
        color: #000;
        border-radius: 1px solid #000;
      }
    }
    &__primary {
      color: #fff;
      background: #307fe2;
      border: 1px solid #307fe2;
      &:hover {
        background: #000;
        border: 1px solid #000;
        color: #fff;
      }
    }

    &__disabled {
      background: #dfe4ee;
      color: #A1ABBE;
      border: 1px solid #dfe4ee;
      cursor: default;
      pointer-events: none;
    }

  }

	.message {
      &__error {
      padding: 10px;
      color: #e76049;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 300px;
  	}
    &__success {
      padding: 10px;
      color: #2eb398;
    }
	}

  .wrapper {
    padding: 5px;
  }
</style>
